/**
 * Auth + onboarding guard for authenticated app surfaces.
 */

import { ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { forceLocalSignOut } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useEffectiveRole } from '@/contexts/QAPerspectiveContext'
import { isAuthDebugEnabled } from '@/lib/debug'
import { isOnboardingEnabled } from '@/config/onboarding'
import { Onboarding } from '@/components/onboarding/Onboarding'
import { FullPageLoading } from '@/components/routing/RouteLoading'

interface ProtectedRouteProps {
  children: ReactNode
  skipOnboardingCheck?: boolean
}

export function ProtectedRoute({ children, skipOnboardingCheck = false }: ProtectedRouteProps) {
  const { isLoading, session, accountId, accountData, isAccountLoading } = useAuth()

  useEffectiveRole()

  const [onboardingComplete, setOnboardingComplete] = useState<boolean | null>(null)
  const [accountError, setAccountError] = useState<string | null>(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const location = useLocation()

  useEffect(() => {
    if (!isLoading && !isAccountLoading && onboardingComplete !== null) {
      setIsInitialLoad(false)
    }
  }, [isLoading, isAccountLoading, onboardingComplete])

  useEffect(() => {
    let mounted = true

    const checkOnboarding = () => {
      if (mounted) {
        setAccountError(null)
      }

      if (isLoading || isAccountLoading) {
        return
      }

      if (!isOnboardingEnabled() || skipOnboardingCheck) {
        if (mounted) {
          setOnboardingComplete(true)
        }
        return
      }

      if (!accountData) {
        return
      }

      if (mounted) {
        setOnboardingComplete(accountData.onboarding_completed === true)
      }
    }

    checkOnboarding()

    return () => {
      mounted = false
    }
  }, [accountId, accountData, isLoading, isAccountLoading, skipOnboardingCheck])

  if (isInitialLoad && (isLoading || isAccountLoading)) {
    return <FullPageLoading />
  }

  if (!accountId) {
    if (session && !isAccountLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="w-full max-w-md">
            <div className="bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-200 dark:border-amber-800 rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <svg
                    className="w-6 h-6 text-amber-600 dark:text-amber-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-fluid-lg font-semibold text-amber-900 dark:text-amber-100 mb-2">
                    Setting up your account
                  </h3>
                  <p className="text-fluid-sm text-amber-800 dark:text-amber-200 mb-4">
                    Your account is still being set up. Please wait a moment and we&apos;ll refresh,
                    or try logging in again.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => window.location.reload()}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-fluid-sm font-medium"
                    >
                      Refresh page
                    </button>
                    <a
                      href="/auth/login"
                      className="px-4 py-2 bg-background border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors text-fluid-sm font-medium text-center"
                    >
                      Back to login
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    const isAuthRoute = location.pathname.startsWith('/auth/')
    if (!isAuthRoute) {
      return <Navigate to="/auth/login" state={{ from: location }} replace />
    }
  }

  if (accountError) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-md">
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 shadow-sm">
            <p className="text-fluid-sm text-red-800 mb-4">{accountError}</p>
            <button
              onClick={() => {
                localStorage.clear()
                sessionStorage.clear()
                void forceLocalSignOut().then(() => {
                  window.location.href = '/auth/login'
                })
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-fluid-sm font-medium"
            >
              Login Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!onboardingComplete && !skipOnboardingCheck && isOnboardingEnabled()) {
    if (isAuthDebugEnabled()) console.log('[Onboarding] Showing individual onboarding')
    return (
      <Onboarding
        isOpen={true}
        onComplete={() => {
          window.location.replace('/')
        }}
      />
    )
  }

  return <>{children}</>
}
